import { Handle, Position } from "@xyflow/react";
import type { ReactNode } from "react";

import type { Staleness } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { formatInt } from "@/lib/format";

// Freshness is a PIPELINE-HEALTH signal (literally source health), so green/amber/red is allowed
// here — it is NOT the device-RAG law. FRESH=green, RECENT=amber, STALE=red; an unknown/unmeasured
// surface falls back to a neutral muted dot so we never imply a health we don't have.
export type DsFreshness = Staleness | "UNKNOWN";

export const dsFreshnessColor = (freshness: DsFreshness): string => {
  if (freshness === "FRESH") {
    return RAG.OK;
  }
  if (freshness === "RECENT") {
    return RAG.WARN;
  }
  if (freshness === "STALE") {
    return RAG.CRITICAL;
  }
  return "var(--br-text-muted)";
};

export const dsFreshnessLabel = (freshness: DsFreshness): string => {
  if (freshness === "FRESH") {
    return "Fresh";
  }
  if (freshness === "RECENT") {
    return "Recent";
  }
  if (freshness === "STALE") {
    return "Stale";
  }
  return "No signal";
};

// Cadence the surface is EXPECTED to refresh on — drives how to read its freshness. A "daily"
// rollup that's a day old is fine; a "continuous" stream that's a day old is broken.
export type DsCadence = "continuous" | "daily" | "monthly" | "static";

export const DS_NODE_WIDTH = 220;
export const DS_NODE_HEIGHT = 92;

export interface DsFreshnessNodeData {
  readonly table: string;
  readonly dataset: string;
  readonly freshness: DsFreshness;
  // A plain display label, not the closed DsCadence union — the live /api/erd graph reports
  // real cadence labels per object (e.g. "event_stream", "daily_rollup", "reference") that don't
  // map 1:1 onto the DsPipelineHealth surface-cadence vocabulary.
  readonly cadence: string;
  readonly rowCount: number | null;
  readonly [key: string]: unknown;
}

// Custom @xyflow/react node: table name + dataset + freshness dot + row count. The node border is
// colored by freshness (per pipeline-health), and source/target handles let elk-routed edges and
// the broken-join edges attach on either side.
export const DsFreshnessNode = ({ data }: { readonly data: DsFreshnessNodeData }): ReactNode => {
  const color = dsFreshnessColor(data.freshness);
  return (
    <div
      style={{
        width: DS_NODE_WIDTH,
        minHeight: DS_NODE_HEIGHT,
        padding: "10px 12px",
        background: "var(--br-surface-1)",
        border: `1px solid ${color}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: "var(--br-radius-md)",
        boxShadow: "var(--br-shadow-card)",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: color, border: "none" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: "var(--br-font-mono)",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--br-text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={data.table}
        >
          {data.table}
        </span>
      </div>
      <div style={{ fontSize: 10, color: "var(--br-text-muted)", marginBottom: 6 }}>
        {data.dataset} · {data.cadence}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color }}>
          {dsFreshnessLabel(data.freshness)}
        </span>
        <span
          className="br-tabular-nums"
          style={{ fontSize: 11, color: "var(--br-text-secondary)" }}
        >
          {data.rowCount === null ? "— rows" : `${formatInt(data.rowCount)} rows`}
        </span>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: color, border: "none" }}
      />
    </div>
  );
};
